import { apiUrl } from "../utils/api";

/**
 * Kommentare an Stellen eines Dokuments (PDF). Sie liegen in KMVS, nicht in
 * der Datei — Backend: `apps/api/src/dokument-kommentare/`.
 *
 * Koordinaten wie im PDF-Editor: PDF-Userspace (pt, Ursprung unten links),
 * Seiten 0-basiert.
 */
export type KommentarRect = [number, number, number, number];

/** Spiegel von `DokumentKommentarDto` im Backend. */
export interface DokumentKommentar {
    id: number;
    parentId: number | null;
    /** `null` = „ohne Stelle“ (Seite beim Umbau gelöscht). */
    seite: number | null;
    rect: KommentarRect | null;
    /** Markierter Text an der Stelle ('' bei Bereich/Punkt). */
    zitat: string;
    text: string;
    autorUserId: string;
    autorName: string;
    erledigt: boolean;
    erstellt: string | null;
    geaendert: string | null;
    /** Text ändern — nur der Autor. */
    darfBearbeiten: boolean;
    /** Löschen — Autor oder Leitung. */
    darfLoeschen: boolean;
}

/** Wurzelkommentar mit seinen Antworten. */
export interface KommentarFaden extends DokumentKommentar {
    antworten: DokumentKommentar[];
}

export interface KommentarStelle {
    seite: number;
    rect: KommentarRect;
    zitat: string;
}

/** Der Server sagt, was nicht passt — das reichen wir durch. */
async function antwort<T>(res: Response, fallback: string): Promise<T> {
    if (!res.ok) {
        const grund = await res
            .json()
            .then((b: { message?: unknown }) =>
                Array.isArray(b?.message) ? b.message.join(", ") : typeof b?.message === "string" ? b.message : "",
            )
            .catch(() => "");
        throw new Error(grund || `${fallback}: ${res.status}`);
    }
    return res.json() as Promise<T>;
}

const json = (method: string, body: unknown): RequestInit => ({
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
});

const pfad = (docId: number, rest = "") => apiUrl(`documents/${docId}/kommentare${rest}`);

export const dokumentKommentarService = {
    async liste(docId: number): Promise<KommentarFaden[]> {
        return antwort(await fetch(pfad(docId)), "Kommentare konnten nicht geladen werden");
    },
    /** Neuer Kommentar an einer Stelle — Antwort ist der neue Faden. */
    async anlegen(docId: number, e: KommentarStelle & { text: string }): Promise<KommentarFaden> {
        return antwort(await fetch(pfad(docId), json("POST", e)), "Kommentar konnte nicht gespeichert werden");
    },
    /** Antwort — Antwort ist der ganze Faden. */
    async antworten(docId: number, wurzelId: number, text: string): Promise<KommentarFaden> {
        return antwort(
            await fetch(pfad(docId), json("POST", { parentId: wurzelId, text })),
            "Antwort konnte nicht gespeichert werden",
        );
    },
    async aendern(docId: number, id: number, a: { text?: string; erledigt?: boolean }): Promise<KommentarFaden> {
        return antwort(await fetch(pfad(docId, `/${id}`), json("PUT", a)), "Änderung fehlgeschlagen");
    },
    async loeschen(docId: number, id: number): Promise<void> {
        await antwort(await fetch(pfad(docId, `/${id}`), { method: "DELETE" }), "Löschen fehlgeschlagen");
    },
    /** Nach einem Seitenumbau: alte Seite i → neue Seite zuordnung[i] (-1 = gelöscht). */
    async seitenZuordnen(docId: number, zuordnung: number[]): Promise<KommentarFaden[]> {
        return antwort(
            await fetch(pfad(docId, "/seiten"), json("PUT", { zuordnung })),
            "Kommentare konnten den Seiten nicht zugeordnet werden",
        );
    },
};
